<script>
import {cloneDeep} from 'lodash';
import {timeSince} from '@/state/helpers';
import HotelDatePicker from "vue-hotel-datepicker";


export default {
  props: {
    campaign: {
      type: Object,
      required: true,
    },
  },
  name: "campaignPosts",
  components: {
    HotelDatePicker,
  },
  data() {
    return {
      dateOptions: {year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: false},
      hotelDateLabels: {
        night: 'day',
        nights: 'days',
        'day-names': ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        'check-in': 'Start',
        'check-out': 'End',
        'month-names': ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
      },
      postsPerPage: 1,
      shownPosts: [],
      selectedSocialNetworks: [],
      selectedInfluencerIds: [],
      fromDate: null,
      toDate: null,
    };
  },
  computed: {
    timeAgo() {
      const dates = this.filteredPosts.filter(post => !!post.info.posts_touched_at)
          .map(post => new Date(post.info.posts_touched_at))

      // There is no posts or posts_touched_at field is missing.
      // But we have surely run the "update job" at least yesterday if not today.
      if (dates.length === 0) {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate()-1)
        return timeSince(yesterday)
      }

      return timeSince(new Date(Math.min.apply(null,dates)))
    },
    influencers() {
      const influencers = {};
      this.posts.forEach(post => {
        influencers[post.info.influencer_profile_id] = {
          id: post.info.influencer_profile_id,
          username: post.info.influencer_main_username,
          profilePictureUrl: post.info.influencer_main_profile_picture_url
        }
      })

      return Object.values(influencers)
    },
    posts() {
      const allPosts = this.$store.state.campaign.postsByCampaign[this.$route.params.campaignId || this.$route.params.id]
      if (!allPosts) {
        return [];
      }

      return cloneDeep(allPosts).map((post) => {
        if (post.insights.engagement_rate) {
          post.insights.engagement_rate = post.insights.engagement_rate + '%';
        }

        post.info.published_at_date_instance = new Date(post.info.published_at)
        post.info.published_at = new Date(post.info.published_at_date_instance).toLocaleDateString(undefined, this.dateOptions);
        return post;
      });
    },
    filteredPosts() {
      return this.posts.filter(post => {
        if (this.selectedSocialNetworks.length > 0 && !this.selectedSocialNetworks.includes(post.info.social_network)) {
          return false;
        }

        const publishedAt = post.info.published_at_date_instance
        if (this.fromDate && this.fromDate > publishedAt) {
          return false
        }
        if (this.toDate && this.toDate < publishedAt) {
          return false;
        }

        if (this.selectedInfluencerIds.length > 0 && !this.selectedInfluencerIds.includes(post.info.influencer_profile_id)) {
          return false
        }

        return true
      })
    }
  },
  methods: {
    updateFromDate(date) {
      this.fromDate = date;
    },
    updateToDate(date) {
      this.toDate = date
    },
  }
}
</script>

<template>
  <div style="min-height: 450px" class="hide-checkbox-inputs">
    <div v-if="filteredPosts.length > 0" class="text-muted last-updated text-right mb-2">
      <i class="bx bx-time-five" style="vertical-align:middle;"></i> Updated {{ timeAgo }} ago
    </div>
    <div class="row mt-2">
      <div class="col-xs-12 col-sm-7 col-md-8 col-lg-6 col-xl-3 mb-1 d-flex flex-row">
        <HotelDatePicker
            v-if="true"
            ref="hotelDatePicker"
            :startingDateValue="new Date(this.campaign.start_date)"
            :endingDateValue="new Date(this.campaign.end_date)"
            :startDate="new Date(this.campaign.start_date)"
            format="DD.MM.YYYY."
            @check-in-changed="updateFromDate"
            @check-out-changed="updateToDate"
            required
            :i18n="hotelDateLabels"/>
      </div>
      <div class="col-xl-9 d-flex text-center" v-if="posts.length > 0">
        <span class="d-none vertical-divider d-xl-inline mx-2" style="margin-left: -6px!important;"></span>

        <b-form-checkbox-group v-model="selectedSocialNetworks" name="socialNetworks">
          <b-form-checkbox value="instagram" class="mr-1 p-0">
            <span class="avatar-xs letter-in-circle border-1 d-inline-block text-center" :class="{'checked-social-network': selectedSocialNetworks.includes('instagram')}" style="background: transparent">
                <img src="@/assets/images/brands/instagram.png" class="img-fluid" alt="Instagram">
            </span>
          </b-form-checkbox>
          <b-form-checkbox value="facebook" class="mr-1 p-0">
            <span class="avatar-xs letter-in-circle border-1 d-inline-block text-center" :class="{'checked-social-network': selectedSocialNetworks.includes('facebook')}" style="background: transparent">
              <i class="bx bxl-facebook-square pop-on-hover" style="color: #3A5897;vertical-align:middle; font-size:2em;"></i>
            </span>
          </b-form-checkbox>
          <b-form-checkbox value="youtube" class="mr-1 p-0">
            <span class="avatar-xs letter-in-circle border-1 d-inline-block text-center" :class="{'checked-social-network': selectedSocialNetworks.includes('youtube')}" style="background: transparent">
              <i class="bx bxl-youtube pop-on-hover" style="color: #FF0000;vertical-align:middle; font-size:2em;"></i>
            </span>
          </b-form-checkbox>
          <b-form-checkbox value="blog" class="mr-1 p-0">
            <span class="avatar-xs letter-in-circle border-1 d-inline-block text-center" :class="{'checked-social-network': selectedSocialNetworks.includes('blog')}" style="background: transparent">
              <img src="@/assets/images/brands/blog.png" class="img-fluid" alt="Blog">
            </span>
          </b-form-checkbox>
        </b-form-checkbox-group>

        <span class="vertical-divider d-inline mx-2"></span>

        <b-form-checkbox-group v-model="selectedInfluencerIds" class="influencersPictures ml-1">
          <b-form-checkbox class="pop-on-hover mr-1 p-0" v-for="(influencer, index) in influencers.slice(0,3)" :key="influencer.id" :value="influencer.id"
                           v-b-tooltip.hover.bottom
                           :title="influencer.username">
            <span :class="{'ml-minus-10': index!==0}">
              <img v-if="influencer.profilePictureUrl" :src="`${influencer.profilePictureUrl}`"
                   class="pop-on-hover rounded-circle avatar-xs border-1" :alt="influencer.username" :class="{'checked-influencer': selectedInfluencerIds.includes(influencer.id)}"/>
              <span v-else class="pop-on-hover avatar-xs letter-in-circle border-1 d-inline-block" :class="{'checked-influencer': selectedInfluencerIds.includes(influencer.id)}" style="background: gray">
                {{influencer.username.charAt(0)}}
              </span>
            </span>

          </b-form-checkbox>

          <b-dropdown v-if="influencers.length > 3" right variant="black" toggle-class="header-item" class="additionalInfluencers">
            <template v-slot:button-content>
              <span class="avatar-xs letter-in-circle border-1 ml-1 d-inline-block p-0" style="background: gray">
                +{{influencers.length - 3}}
              </span>
            </template>

            <label class="dropdown-item has-pointer-cursor pb-0" v-for="subInfluencer in influencers.slice(3)" :key="subInfluencer.id">
              <b-form-checkbox :value="subInfluencer.id" class="d-flex p-0">
                <img v-if="subInfluencer.profilePictureUrl" :src="`${subInfluencer.profilePictureUrl}`"
                     class="rounded-circle avatar-xs border-1 ml-1" :alt="subInfluencer.username" :class="{'checked-influencer': selectedInfluencerIds.includes(subInfluencer.id)}"/>
                <span v-else class="avatar-xs letter-in-circle border-1 mr-1 mb-1 d-inline-block" :class="{'checked-influencer': selectedInfluencerIds.includes(subInfluencer.id)}" style="background: gray">
                  {{subInfluencer.username.charAt(0)}}
                </span>
                {{subInfluencer.username}}
              </b-form-checkbox>
            </label>
          </b-dropdown>
        </b-form-checkbox-group>
      </div>
    </div>
    <div v-if="filteredPosts.length === 0" class="text-center has-text-weight-bold">
      <h3 class="p-4">Posts not found.</h3>
    </div>
    <div v-for="post in filteredPosts" :key="post.published_at" class="tab-section p-3 mt-4">
      <div class="row">
        <div class="col-12 col-md-auto text-center">
          <div class="image-container">
            <img :src="post.info.media_url" alt class="img-fluid" style="max-height:250px;margin: auto;"/>
          </div>
        </div>
        <div class="col-12 col-md mt-4 mt-md-0">
          <div class="row flex-column justify-content-end">
            <div class="col mt-3">
              <div class="row">
                <div class="col">
                  <h5 class="m-0 timestamp">{{ post.info.published_at }}</h5>
                </div>
                <div class="col-auto">
                  <router-link tag="a" :to="`/client/influencers/${post.info.influencer_profile_id}`">
                    <span class="username">{{ post.info.username }}</span>
                    <img v-if="post.info.profile_picture_url" :src="`${post.info.profile_picture_url}`"
                         class="rounded-circle avatar-xs border-1 ml-1" :alt="post.info.username"/>
                    <div v-else-if="post.info.username"
                         class="avatar-xs letter-in-circle border-1 ml-1 d-inline-block text-uppercase"
                         style="background: gray">{{ post.info.username.charAt(0) }}
                    </div>
                  </router-link>

                  <div v-if="post.info.social_network === 'instagram' && post.info.post_type === 'STORY'"
                       class="text-right">
                    <span class="badge  badge-soft-secondary font-size-12 mt-2 align-bottom">Story</span>
                  </div>
                </div>
                <div class="col-12 mb-xl-2 mt-2 d-flex align-items-center">
                  <a :href="post.info.permalink" target="_blank">
                    Visit post
                    <div v-if="post.info.social_network === 'instagram'" class="post-icon">
                      <img src="@/assets/images/brands/instagram.png" class="img-fluid" alt="">
                    </div>
                    <i v-else-if="post.info.social_network === 'facebook'" class="bx bxl-facebook-square mr-1"
                       style="color: #3A5897;vertical-align:top; font-size:1.35em;"></i>
                    <i v-else-if="post.info.social_network === 'youtube'" class="bx bxl-youtube mr-1"
                       style="color: #FF0000;vertical-align:top; font-size:1.35em;"></i>

                  </a>
                </div>
                <div class="col-12">
                  <p class="caption text-muted my-3">{{ post.info.title }}</p>
                </div>
              </div>
            </div>
            <div class="col mt-0 mt-xl-2">
              <div class="data-container pt-2">
                <div class="row">
                  <div class="col-6 col-xl-2 mb-2 mb-xl-0 card-data"
                       :class="{'d-none d-xl-block': [4,5].includes(index)}" v-for="(data, key, index) in post.insights"
                       :key="key">
                    <div class="row">
                      <div class="col-12 mb-0 mb-xl-1">
                        {{ (key.charAt(0).toUpperCase() + key.slice(1)).replace(/_/g, " ") }}
                      </div>
                      <div class="col-12 h-100">
                        <h5 class="mb-0" v-if="data">{{ data.toLocaleString() }}</h5>
                        <h5 class="mb-0" v-else>0</h5>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.vertical-divider {
  border-left: 1px solid grey;
  height: 60%;
  margin-top: 5px;
  vertical-align: middle;
  float: left;
}
.ml-minus-10 {
  margin-left: -10px;
}

.additionalInfluencers>button {
  height: auto;
  padding: 0 !important; /*fix*/
}

.checked-social-network {
  border-radius: 50%;
  border: 1px solid gray;
}
.pop-on-hover:hover {
  margin-top: -7px !important;
  z-index: 99999;
}

.avatar-xs img {
  height: 20px;
}
.avatar-xs:hover img {
  transform: translate(0, -5px);
}
.post-icon {
  display: inline-block;
  height: 14px;
  width: 14px;
  margin-left: 1px;
  transform: translate(0, -1px);
}
.post-icon img:hover {
  bottom: 0px;
}




</style>
